import React, { useState, useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { QRCodeSVG } from "qrcode.react";
import { Search, Printer, FileText, X, Bluetooth } from "lucide-react";
import { formatCurrency, formatDate, COMMITTEE } from "../utils/mockData";
import { donationsApi } from "../utils/api";
import { btPrintReceipt, isBluetoothSupported } from "../utils/btPrint";

const THERMAL_WIDTH = "54mm";

const S = {
  receipt: {
    width: THERMAL_WIDTH,
    fontFamily: "'Courier New', Courier, monospace",
    fontSize: "8pt",
    lineHeight: 1.3,
    color: "#000",
    backgroundColor: "#fff",
    padding: "2mm",
    margin: "0 auto",
    boxSizing: "border-box",
    wordBreak: "break-word",
  },
  center: { textAlign: "center" },
  bold: { fontWeight: "bold" },
  dashedTop: { borderTop: "1px dashed #000", paddingTop: "1.5mm", marginTop: "1.5mm" },
  dashedBottom: { borderBottom: "1px dashed #000", paddingBottom: "1.5mm", marginBottom: "1.5mm" },
  row: { display: "flex", justifyContent: "space-between", fontSize: "7pt" },
  label: { fontSize: "6pt", textTransform: "uppercase", letterSpacing: "0.5px" },
  amountBox: {
    textAlign: "center",
    borderTop: "1px dashed #000",
    borderBottom: "1px dashed #000",
    padding: "2mm 0",
    margin: "2mm 0",
  },
  amountNum: { fontSize: "16pt", fontWeight: "bold", letterSpacing: "1px" },
  footer: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: "2mm" },
  sigBlock: { fontSize: "7pt" },
  sigLine: { borderTop: "1px solid #000", paddingTop: "0.5mm", marginTop: "7mm", fontWeight: "bold" },
  smallText: { fontSize: "6pt" },
  footNote: { textAlign: "center", fontSize: "6pt", borderTop: "1px dashed #000", marginTop: "2mm", paddingTop: "1.5mm" },
};

function amountInWords(n) {
  if (!n || n === 0) return "Zero";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  if (n < 20) return ones[n];
  if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
  if (n < 1000) return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + amountInWords(n % 100) : "");
  if (n < 100000) return amountInWords(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + amountInWords(n % 1000) : "");
  if (n < 10000000) return amountInWords(Math.floor(n / 100000)) + " Lakh" + (n % 100000 ? " " + amountInWords(n % 100000) : "");
  return amountInWords(Math.floor(n / 10000000)) + " Crore" + (n % 10000000 ? " " + amountInWords(n % 10000000) : "");
}

function ReceiptView({ donation }) {
  if (!donation) return null;
  return (
    <div style={S.receipt}>
      {/* Header */}
      <div style={{ ...S.center, ...S.dashedBottom }}>
        <img src="/logo.png" alt="Logo" style={{ height: "12mm", width: "12mm", display: "block", margin: "0 auto 1mm" }} onError={(e) => { e.target.style.display = "none"; }} />
        <div style={{ fontSize: "9pt", fontWeight: "bold", lineHeight: 1.2 }}>{COMMITTEE.name}</div>
        <div style={S.smallText}>{COMMITTEE.address}</div>
        <div style={S.smallText}>Ph: {COMMITTEE.phone}</div>
      </div>

      {/* Title */}
      <div style={{ ...S.center, ...S.bold, fontSize: "8pt", border: "1px solid #000", padding: "1mm", marginBottom: "2mm", letterSpacing: "2px" }}>
        DONATION RECEIPT
      </div>

      {/* Receipt No & Date */}
      <div style={S.row}>
        <span><span style={S.label}>Receipt# </span>{donation.receiptNo}</span>
        <span>{formatDate(donation.date)}{donation.time ? ` ${donation.time}` : ""}</span>
      </div>

      {/* Donor details */}
      <div style={S.dashedTop}>
        <div><span style={S.label}>Name: </span><strong>{donation.donorName}</strong></div>
        <div><span style={S.label}>Phone: </span>{donation.phone}</div>
        {donation.address && <div><span style={S.label}>Addr: </span>{donation.address}</div>}
        <div><span style={S.label}>Mode: </span>{donation.paymentMode}</div>
      </div>

      {/* Amount */}
      <div style={S.amountBox}>
        <div style={{ ...S.label, marginBottom: "1mm" }}>Amount Received</div>
        <div style={S.amountNum}>{formatCurrency(Number(donation.amount || 0))}</div>
        <div style={{ ...S.smallText, marginTop: "1mm", fontStyle: "italic" }}>
          {amountInWords(Number(donation.amount || 0))} Rupees Only
        </div>
      </div>

      {/* QR Code */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginTop: "2mm" }}>
        <QRCodeSVG value={COMMITTEE.communitylink} size={70} level="M" />
        <div style={{ ...S.smallText, marginTop: "0.5mm" }}>Join WhatsApp Channel</div>
      </div>

      {donation.receivedBy && (
        <div style={{ ...S.smallText, marginTop: "2mm" }}>Added by: {donation.receivedBy}</div>
      )}

      {/* Footer */}
      <div style={S.footNote}>
        <div>Ganesh Chaturthi 2026 — utsav.btrix.in</div>
        <div>Computer Generated Receipt</div>
      </div>
    </div>
  );
}

export default function Receipts() {
  const [donations, setDonations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [btStatus, setBtStatus] = useState("idle"); // idle | printing | done | error
  const [btError, setBtError] = useState("");
  const printRef = useRef();

  useEffect(() => {
    donationsApi.list()
      .then((res) => setDonations(res.donations || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleBtPrint = async () => {
    setBtStatus("printing");
    setBtError("");
    try {
      await btPrintReceipt(selectedDonation, COMMITTEE);
      setBtStatus("done");
      setTimeout(() => setBtStatus("idle"), 3000);
    } catch (e) {
      setBtError(e.message || "Bluetooth print failed");
      setBtStatus("error");
      setTimeout(() => setBtStatus("idle"), 5000);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: `@page { size: 58mm auto; margin: 0; } @media print { html, body { width: 58mm; margin: 0; padding: 0; } }`,
  });

  const filtered = donations.filter((d) =>
    d.donorName.toLowerCase().includes(search.toLowerCase()) ||
    (d.receiptNo || "").toLowerCase().includes(search.toLowerCase()) ||
    (d.phone || "").includes(search)
  );

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="p-4 lg:p-6 space-y-4 fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Receipts</h1>
          <p className="text-sm text-gray-500">{donations.length} receipts generated</p>
        </div>
      </div>

      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, receipt number, phone..."
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white"
        />
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        {/* Desktop Table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Receipt No</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Donor</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Date</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Mode</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Purpose</th>
                <th className="text-left text-xs font-semibold text-gray-500 px-4 py-3">Received By</th>
                <th className="text-right text-xs font-semibold text-gray-500 px-4 py-3">Amount</th>
                <th className="text-center text-xs font-semibold text-gray-500 px-4 py-3">Print</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((d) => (
                <tr key={d.id} className="hover:bg-gray-50/50 transition-colors cursor-pointer" onClick={() => setSelectedDonation(d)}>
                  <td className="px-4 py-3">
                    <span className="text-xs font-mono text-orange-600 font-semibold">{d.receiptNo}</span>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-semibold text-gray-800">{d.donorName}</p>
                    <p className="text-xs text-gray-400">{d.phone}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">{formatDate(d.date)}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{d.paymentMode}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">{d.purpose}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{d.receivedBy || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-sm font-bold text-green-600">{formatCurrency(Number(d.amount || 0))}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedDonation(d); }}
                      className="inline-flex items-center gap-1 text-xs text-orange-500 font-semibold bg-orange-50 hover:bg-orange-100 px-2.5 py-1.5 rounded-lg transition-colors"
                    >
                      <Printer size={12} /> Print
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile List */}
        <div className="md:hidden divide-y divide-gray-100">
          {filtered.map((d) => (
            <div key={d.id} className="px-4 py-3 flex items-center gap-3" onClick={() => setSelectedDonation(d)}>
              <div className="w-9 h-9 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <FileText size={16} className="text-orange-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{d.donorName}</p>
                <p className="text-xs text-gray-400">{d.receiptNo} • {formatDate(d.date)}{d.receivedBy ? ` • ${d.receivedBy}` : ""}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-bold text-green-600">{formatCurrency(Number(d.amount || 0))}</p>
                <button onClick={(e) => { e.stopPropagation(); setSelectedDonation(d); }}
                  className="text-[10px] text-orange-500 font-semibold">Print</button>
              </div>
            </div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-12 text-gray-400 text-sm">No receipts found</div>
        )}
      </div>

      {/* Receipt Modal */}
      {selectedDonation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedDonation(null)} />
          <div className="relative bg-gray-100 rounded-2xl shadow-2xl z-10 w-full max-w-xs max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 rounded-t-2xl z-10">
              <div className="flex items-center justify-between mb-1">
                <h2 className="text-xs font-bold text-gray-700 font-mono">{selectedDonation.receiptNo}</h2>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePrint}
                    className="flex items-center gap-1.5 bg-orange-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-orange-600"
                  >
                    <Printer size={12} /> Print
                  </button>
                  {isBluetoothSupported() && (
                    <button
                      onClick={handleBtPrint}
                      disabled={btStatus === "printing"}
                      className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${
                        btStatus === "done" ? "bg-green-500 text-white" :
                        btStatus === "error" ? "bg-red-500 text-white" :
                        btStatus === "printing" ? "bg-blue-400 text-white opacity-70" :
                        "bg-blue-600 text-white hover:bg-blue-700"
                      }`}
                    >
                      <Bluetooth size={12} />
                      {btStatus === "printing" ? "Printing…" : btStatus === "done" ? "Sent!" : btStatus === "error" ? "Failed" : "BT Print"}
                    </button>
                  )}
                  <button onClick={() => { setSelectedDonation(null); setBtStatus("idle"); }} className="p-1.5 hover:bg-gray-100 rounded-lg">
                    <X size={16} className="text-gray-500" />
                  </button>
                </div>
              </div>
              {btStatus === "error" && btError && (
                <p className="text-[10px] text-red-500 mt-0.5">{btError}</p>
              )}
            </div>
            <div className="flex justify-center py-4 bg-gray-100">
              <div ref={printRef} className="bg-white shadow-md">
                <ReceiptView donation={selectedDonation} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
